import React, { useEffect, useState } from 'react';
import axios from '../api';
import rawAxios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Stack,
  Divider,
  Collapse,
  Link,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
  Devices,
  VerifiedUser,
  Insights,
} from '@mui/icons-material';

const SSO_ERRORS = {
  user_not_found: 'No account matches that SSO email. Ask an admin to invite you first.',
  user_inactive: 'Your account is inactive. Contact an administrator.',
  no_email: 'SSO provider did not return an email address.',
  microsoft_disabled: 'Microsoft sign-in is not enabled.',
  google_disabled: 'Google sign-in is not enabled.',
  invalid_state: 'SSO session expired. Please try again.',
  microsoft_token_failed: 'Microsoft sign-in failed. Please try again.',
  google_token_failed: 'Google sign-in failed. Please try again.',
  microsoft_callback_failed: 'Microsoft sign-in failed. Please try again.',
  google_callback_failed: 'Google sign-in failed. Please try again.',
  microsoft_start_failed: 'Could not start Microsoft sign-in.',
  google_start_failed: 'Could not start Google sign-in.',
  google_profile_failed: 'Google did not return a usable profile.',
};

function AdminLogin({ onLogin }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoBusy, setSsoBusy] = useState('');
  const [error, setError] = useState('');
  const [providers, setProviders] = useState({ microsoft: false, google: false });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryMsg, setRecoveryMsg] = useState('');
  const [recoveryErr, setRecoveryErr] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  useEffect(() => {
    if (email) {
      localStorage.setItem('email', email.toLowerCase().trim());
    }
  }, [email]);

  useEffect(() => {
    const code = searchParams.get('ssoError');
    if (code) {
      setError(
        SSO_ERRORS[code] ||
          'Sign-in with SSO failed. Please try again or use email and password.'
      );
      const next = new URLSearchParams(searchParams);
      next.delete('ssoError');
      setSearchParams(next, { replace: true });
    }
    axios
      .get('/auth/providers', { skipAuthRedirect: true })
      .then((res) => setProviders(res.data.result || {}))
      .catch(() => {});
  }, [searchParams, setSearchParams]);

  const apiRoot = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const startSso = (provider) => {
    setSsoBusy(provider);
    window.location.href = `${apiRoot}/auth/${provider}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/undertaking/dashboard/login', {
        username: email,
        password,
      });

      if (response.data.success) {
        if (response.data.token) {
          localStorage.setItem('adminToken', response.data.token);
        }
        if (response.data.user) {
          localStorage.setItem('adminUser', JSON.stringify(response.data.user));
        }
        onLogin(response.data.user || null, {
          requirePasswordChange: !!response.data.requirePasswordChange,
        });
      } else {
        setError('Invalid login credentials');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 401) {
        setError('Invalid email or password. Please check your credentials.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const submitRecovery = async (mode) => {
    const addr = recoveryEmail.trim().toLowerCase();
    if (!addr.includes('@')) {
      setRecoveryErr('Enter a valid email address.');
      return;
    }
    setRecoveryLoading(true);
    setRecoveryErr('');
    setRecoveryMsg('');
    try {
      const path = mode === 'forgot' ? '/users/forgotPassword' : '/users/resend-activation';
      const res = await rawAxios.post(`${API}${path}`, { email: addr });
      setRecoveryMsg(res.data?.message || 'If an account exists, an email has been sent.');
    } catch (err) {
      setRecoveryErr(
        err.response?.data?.message || 'Request failed. Please try again.'
      );
    } finally {
      setRecoveryLoading(false);
    }
  };

  const features = [
    { icon: <Devices />, title: 'Full asset lifecycle', desc: 'Track from procurement to disposal.' },
    { icon: <VerifiedUser />, title: 'Digital undertakings', desc: 'Signed, auditable acknowledgements.' },
    { icon: <Insights />, title: 'Live analytics', desc: 'Value, warranty and location insights.' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
        background:
          'radial-gradient(1200px 600px at 10% 10%, #eef2ff 0%, transparent 50%), radial-gradient(1000px 500px at 90% 90%, #fae8ff 0%, transparent 50%), #eef1f8',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: 900,
          minHeight: 540,
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        {/* Brand panel */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '46%',
            p: 5,
            color: '#fff',
            position: 'relative',
            background: 'linear-gradient(150deg,#312e81 0%,#4f46e5 45%,#7c3aed 100%)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.15,
              backgroundImage:
                'radial-gradient(circle at 20% 20%, #fff 0, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0, transparent 35%)',
            }}
          />
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.16)',
                backdropFilter: 'blur(6px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 22,
                mb: 3,
              }}
            >
              A
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Asset Undertaking System
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Manage devices, ownership and compliance in one place.
            </Typography>
          </Box>

          <Stack spacing={2.5} sx={{ position: 'relative', mt: 4 }}>
            {features.map((f) => (
              <Box key={f.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.16)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {f.title}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {f.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Form panel */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                background: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
              }}
            >
              <LockOutlinedIcon />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Welcome back
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 0.5 }}>
              Sign in to the admin dashboard.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 2.5, py: 1.2 }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1.5 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => {
                setForgotOpen((v) => !v);
                setResendOpen(false);
                setRecoveryEmail(email);
                setRecoveryMsg('');
                setRecoveryErr('');
              }}
            >
              Forgot password?
            </Link>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => {
                setResendOpen((v) => !v);
                setForgotOpen(false);
                setRecoveryEmail(email);
                setRecoveryMsg('');
                setRecoveryErr('');
              }}
            >
              Resend activation email
            </Link>
          </Stack>

          <Collapse in={forgotOpen}>
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Reset password
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Work email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              {recoveryErr && forgotOpen && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {recoveryErr}
                </Alert>
              )}
              {recoveryMsg && forgotOpen && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {recoveryMsg}
                </Alert>
              )}
              <Button
                variant="outlined"
                fullWidth
                disabled={recoveryLoading}
                onClick={() => submitRecovery('forgot')}
              >
                {recoveryLoading ? 'Sending…' : 'Send reset link'}
              </Button>
            </Box>
          </Collapse>

          <Collapse in={resendOpen}>
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Resend activation
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                For new accounts that have not set a password yet.
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Work email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              {recoveryErr && resendOpen && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {recoveryErr}
                </Alert>
              )}
              {recoveryMsg && resendOpen && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {recoveryMsg}
                </Alert>
              )}
              <Button
                variant="outlined"
                fullWidth
                disabled={recoveryLoading}
                onClick={() => submitRecovery('resend')}
              >
                {recoveryLoading ? 'Sending…' : 'Resend activation link'}
              </Button>
            </Box>
          </Collapse>

          {(providers.microsoft || providers.google) && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Or continue with
                </Typography>
              </Divider>
              <Stack spacing={1}>
                {providers.microsoft && (
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={!!ssoBusy || loading}
                    onClick={() => startSso('microsoft')}
                  >
                    {ssoBusy === 'microsoft' ? 'Redirecting…' : 'Continue with Microsoft'}
                  </Button>
                )}
                {providers.google && (
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={!!ssoBusy || loading}
                    onClick={() => startSso('google')}
                  >
                    {ssoBusy === 'google' ? 'Redirecting…' : 'Continue with Google'}
                  </Button>
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default AdminLogin;
